import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const roleKeys = {
  all: ['roles'],
  lists: () => [...roleKeys.all, 'list'],
  list: (orgId) => [...roleKeys.lists(), orgId],
};

// Fetch roles for an organization (requires org_id header)
export function useRoles(orgId) {
  return useQuery({
    queryKey: roleKeys.list(orgId),
    queryFn: () => apiHelper.getResource('/rbac/org/roles/', { orgId, suppressToast: true }),
    enabled: !!orgId,
    retry: false,
    staleTime: 0,
  });
}

// Create role
// - Super admin: no orgId in header
// - Org admin: pass orgId in header
export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, orgId }) => {
      // If orgId is provided (org admin), pass it in header
      // If not (super admin), don't pass header
      const options = orgId ? { orgId } : {};
      return apiHelper.postResource('/rbac/roles/', data, options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}

// Update role
export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, orgId }) => {
      const options = orgId ? { orgId } : {};
      return apiHelper.putResource(`/rbac/roles/${id}/`, data, options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}

// Delete role
export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId }) => {
      const options = orgId ? { orgId } : {};
      return apiHelper.deleteResource(`/rbac/roles/${id}/`, options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}

// Fetch single role with its permissions
export function useRole(roleId, orgId) {
  return useQuery({
    queryKey: [...roleKeys.all, 'detail', roleId],
    queryFn: async () => {
      const res = await apiHelper.getResource(`/rbac/roles/${roleId}/`, { orgId });
      return res;
    },
    enabled: !!roleId && !!orgId,
    staleTime: 0,
  });
}

// Assigning of bulk permissions to a role
export function useAssignPermissionsToRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissions, orgId }) => {
      const options = orgId ? { orgId } : {};
      return apiHelper.postResource('/rbac/role-permissions/bulk/', { role_id: roleId, permissions }, options);
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['role-permissions-overview', orgId] });
    },
  });
}

// Patch permissions for a specific role
// PATCH /rbac/roles/<role_id>/permissions/
export function usePatchRolePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, permissions, orgId }) => {
      const options = orgId ? { orgId } : {};
      return apiHelper.patchResource(`/rbac/roles/${roleId}/permissions/`, { permissions }, options);
    },
    onSuccess: (_, { roleId, orgId }) => {
      queryClient.invalidateQueries({ queryKey: [...roleKeys.all, 'detail', roleId] });
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['role-permissions-overview', orgId] });
    },
  });
}

// Fetch roles permissions overview for an organization
export function useRolePermissionsOverview(orgId) {
  return useQuery({
    queryKey: ['role-permissions-overview', orgId],
    queryFn: () => apiHelper.getResource(`/rbac/org/${orgId}/roles-perms-overview/`),
    enabled: !!orgId,
  });
}
